import cn from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';

import { MenuItem } from '@/types/menu';

import MenuItemButton from './MenuItemButton';
import MenuItemLink from './MenuItemLink';

type Props = {
  items: MenuItem[];
  activeItem?: string;
  onItemToggle: (itemId: string) => void;
  tabIndex?: number;
  ariaHidden?: boolean;
  nameClassName?: string;
  dropdownClassName?: string;
};

const MenuDropdown: React.FC<Props> = ({
  items,
  activeItem,
  onItemToggle,
  tabIndex,
  ariaHidden,
  nameClassName = 'subname',
  dropdownClassName = 'sub-dropdown'
}) => {
  const router = useRouter();

  return (
    <ul className={dropdownClassName} aria-hidden={ariaHidden}>
      {items.map(item => {
        // availableFrom is Unix timestamp in seconds, Date.now() is in milliseconds
        if (item.options.availableFrom && item.options.availableFrom * 1000 > Date.now()) {
          return null;
        }

        const hasChildren = Object.keys(item.items || {}).length > 0;
        const isActive = item.resource === router.asPath || item.resource === router.route;

        return (
          <li
            key={item.id}
            className={cn(item.options.classes ?? '', {
              open: activeItem === item.id,
              active: isActive,
              'has-children': hasChildren
            })}
          >
            {hasChildren ? (
              <>
                <MenuItemButton
                  item={item}
                  isOpen={activeItem === item.id}
                  tabIndex={tabIndex}
                  ariaHidden={ariaHidden}
                  onClick={() => onItemToggle(item.id)}
                  nameClassName={nameClassName}
                />
                {item.items && (
                  <MenuDropdown
                    items={Object.values(item.items)}
                    activeItem={activeItem}
                    onItemToggle={onItemToggle}
                    tabIndex={activeItem === item.id ? tabIndex : -1}
                    ariaHidden={activeItem !== item.id}
                    nameClassName={nameClassName}
                    dropdownClassName={dropdownClassName}
                  />
                )}
              </>
            ) : (
              <MenuItemLink item={item} tabIndex={tabIndex} ariaHidden={ariaHidden} nameClassName={nameClassName} />
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default MenuDropdown;
